/**
 * Copyright Highway9 Networks Inc.
 */
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";
import { Types } from "../../constants/types";
import { subscriberService } from "../../services";
import { DeviceGroup } from "../../types/deviceGroup";
import { Device } from "~/types/device";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";

type initState = {
  data: DeviceGroup[];
  current: DeviceGroup | null;
  open: boolean;
  deviceAdministrationOpen: boolean;
  isEdit: boolean;
  selectedDeviceGroupIDs: string[];
  selectedDevices: Device[];
  showSyntheticSims: boolean;
};

export const defaultDeviceGroup: DeviceGroup = {
  name: "",
  activePolicies: [],
  ueAmbr: {
    uplink: 0,
    downlink: 0,
  },
  vmzZones: [],
};

const initialState: initState = {
  data: [],
  current: null,
  open: false,
  deviceAdministrationOpen: false,
  selectedDeviceGroupIDs: [Types.all],
  isEdit: false,
  selectedDevices: [],
  showSyntheticSims: false, 
};

export const fetchDeviceGroups = createAsyncThunk(
  "deviceGroup/fetchDeviceGroups",
  async () => {
    try {
      const deviceGroups = await subscriberService.getDeviceGroups();
      return deviceGroups;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY; deviceGroups payload not surfaced in KPI UI
  // (called from VMZ admin + useDevicePage subscribers page + websocket refreshDeviceGroups on connect)
  { condition: () => !isPartnerKpiOnlyUser() },
);

const deviceGroupSlice = createSlice({
  name: "deviceGroups",
  initialState,
  reducers: {
    setValues: (state, action: PayloadAction<DeviceGroup | null>) => {
      state.current = action.payload;
    },
    setDeviceGroupOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setDeviceAdministrationOpen: (state, action: PayloadAction<boolean>) => {
      state.deviceAdministrationOpen = action.payload;
    },
    setSelectedDeviceGroupIDs: (state, action: PayloadAction<string[]>) => {
      state.selectedDeviceGroupIDs = action.payload;
    },
    setEdit: (state, action: PayloadAction<boolean>) => {
      state.isEdit = action.payload;
    },
    setSelectedDevices: (state, action: PayloadAction<Device[]>) => {
      state.selectedDevices = action.payload
    },
    setShowSyntheticSims: (state, action: PayloadAction<boolean>) => {
      state.showSyntheticSims = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDeviceGroups.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

export const deviceGroupActions = deviceGroupSlice.actions;
export default deviceGroupSlice.reducer;

export const subscriberDeviceGroups = createSelector((state: RootState) => state.deviceGroup.data, (state: RootState) => state.deviceGroup.showSyntheticSims, (data, showSyntheticSims) => data.filter((group) => !group.isSynthetic || showSyntheticSims));
export const deviceGroupState = (state: RootState) => state.deviceGroup.current;
export const deviceGroupOpen = (state: RootState) => state.deviceGroup.open;
export const deviceGroupDeviceAdministrationOpen = (state: RootState) => state.deviceGroup.deviceAdministrationOpen;
export const deviceGroupSelectedIDs = (state: RootState) => state.deviceGroup.selectedDeviceGroupIDs;
export const deviceGroupIsEdit = (state: RootState) => state.deviceGroup.isEdit;
export const deviceGroupSelectedDevices = (state: RootState) => state.deviceGroup.selectedDevices;
export const showSyntheticSimsSelector = (state: RootState) => state.deviceGroup.showSyntheticSims;
